import React from "react";
import { motion } from "framer-motion";
import { Eye, Send, Trophy, Zap } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Eye,
    title: "Spot",
    body: [
      "The people around you are the game.",
      "See it. Match it. Claim the spot.",
    ],
    accent: "var(--pw-pink)",
    fg: "#fff",
  },
  {
    n: "02",
    icon: Send,
    title: "Pass",
    body: [
      "Spotted it?",
      "Pass the card — and the points — to someone else.",
    ],
    accent: "var(--pw-yellow)",
    fg: "var(--pw-navy)",
  },
  {
    n: "03",
    icon: Trophy,
    title: "Win",
    body: [
      "Dump your cards. Keep your score low.",
      "Lowest score wins.",
    ],
    accent: "var(--pw-orange)",
    fg: "#fff",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="confetti-canvas py-12 md:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="max-w-3xl mb-8 md:mb-16">
          <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>How It Works</p>
          <h2
            className="font-display font-black leading-[0.95] tracking-tighter text-white"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
          >
            Three steps.
            <br />
            <span className="yellow-stroke" style={{ color: "#fff" }}>That&apos;s the whole game.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-7">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`how-step-${s.n}`}
                className="relative bg-white rounded-2xl p-6 md:p-10 border border-black/5 card-tilt"
              >
                <span
                  className="font-display absolute top-5 right-6 text-sm font-semibold"
                  style={{ color: s.accent }}
                >
                  {s.n}
                </span>
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: s.accent }}
                >
                  <Icon className="w-6 h-6" style={{ color: s.fg }} />
                </div>
                <h3
                  className="font-display font-bold mb-4 tracking-tight text-left"
                  style={{ color: "var(--pw-navy)", fontSize: "clamp(2rem, 3.5vw, 2.75rem)" }}
                >
                  {s.title}<span style={{ color: s.accent }}>.</span>
                </h3>
                <div
                  data-testid={`how-step-body-${s.n}`}
                  className="text-left text-sm md:text-base leading-relaxed space-y-1.5"
                  style={{ color: "rgba(14, 26, 43, 0.72)" }}
                >
                  {s.body.map((line, li) => (
                    <p key={li}>{line}</p>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Chaos aside */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, delay: 0.15 }}
          data-testid="how-chaos-aside"
          className="mt-8 md:mt-10 rounded-2xl p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-5 md:gap-8 border"
          style={{ borderColor: "rgba(255,255,255,0.15)", backgroundColor: "rgba(255,255,255,0.04)" }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: "var(--pw-purple)" }}
            >
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="md:hidden">
              <p className="eyebrow" style={{ color: "var(--pw-purple)" }}>Plus</p>
              <h4 className="font-display text-2xl font-bold text-white tracking-tight">
                Chaos.
              </h4>
            </div>
          </div>
          <div className="hidden md:block">
            <p className="eyebrow" style={{ color: "var(--pw-purple)" }}>Plus</p>
            <h4 className="font-display text-3xl font-bold text-white tracking-tight">
              Chaos<span style={{ color: "var(--pw-purple)" }}>.</span>
            </h4>
          </div>
          <p className="text-white/75 leading-relaxed md:text-lg md:max-w-xl">
            Special cards change the game — swap hands, double the damage or bin a card.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
